<template>
  <div class="slack-notifications-config">
    <el-divider content-position="left">Slack notifications</el-divider>

    <div class="slack-field">
      <label>Webhook URL</label>
      <el-input
        v-model="localConfig.webhookUrl"
        type="password"
        placeholder="https://hooks.slack.com/services/..."
        show-password />
      <small>
        Create a Slack app, enable Incoming Webhooks, add a webhook to a channel,
        then paste the generated URL here.
        <a
          href="https://docs.slack.dev/messaging/sending-messages-using-incoming-webhooks/"
          target="_blank"
          rel="noopener noreferrer">Slack instructions</a>
      </small>
    </div>

    <div class="slack-field">
      <label>Single message</label>
      <el-input
        v-model="localConfig.message"
        type="textarea"
        :rows="3"
        :placeholder="messagePlaceholder" />
      <small>{{ variablesHint }}</small>
    </div>

    <div class="slack-field">
      <label>Digest</label>
      <div class="slack-switch-row">
        <el-switch v-model="localConfig.digest.enabled" />
        <span>Aggregate notifications</span>
      </div>
    </div>

    <div v-if="localConfig.digest.enabled" class="slack-digest-config">
      <div class="slack-field">
        <label>Default window (ms)</label>
        <el-input-number
          v-model="localConfig.digest.defaultWindowMs"
          :min="1000"
          :step="1000" />
      </div>

      <div class="slack-field">
        <label>Default digest message</label>
        <el-input
          v-model="localConfig.digest.defaultMessage"
          type="textarea"
          :rows="4" />
      </div>

      <div
        v-for="(rule, index) in localConfig.digest.rules"
        :key="index"
        class="slack-digest-rule">
        <div class="slack-digest-rule-header">
          <strong>Rule {{ index + 1 }}</strong>
          <el-button
            type="text"
            size="mini"
            @click="removeRule(index)">Remove</el-button>
        </div>

        <div class="slack-field">
          <label>Event types</label>
          <el-checkbox-group v-model="rule.eventTypes">
            <el-checkbox
              v-for="type in eventTypes"
              :key="type"
              :label="type">
              {{ type }}
            </el-checkbox>
          </el-checkbox-group>
        </div>

        <div class="slack-field">
          <label>Window (ms)</label>
          <el-input-number
            v-model="rule.windowMs"
            :min="1000"
            :step="1000" />
        </div>

        <div class="slack-field">
          <label>Message</label>
          <el-input
            v-model="rule.message"
            type="textarea"
            :rows="4" />
        </div>
      </div>

      <el-button
        size="mini"
        type="primary"
        plain
        @click="addRule">Add digest rule</el-button>
    </div>
  </div>
</template>

<script>
const DEFAULT_MESSAGE = ':large_green_circle: *{{eventName}}* - {{status}}\n{{eventLink}}';
const DEFAULT_DIGEST_MESSAGE = '*{{digest.count}} events*\n{{each digest.items}}- {{eventName}} ({{eventType}}) - {{status}}\n{{/each}}';

export default {
  name: 'SlackNotificationsConfigForm',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isSyncing: false,
      eventTypes: ['start', 'stop', 'failed', 'ending_soon'],
      messagePlaceholder: DEFAULT_MESSAGE,
      variablesHint: 'Variables: {{eventName}}, {{status}}, {{eventLink}}, {{input}}, {{outputs}}, {{client}}, {{accountName}}, {{eventType}}',
      localConfig: this.normalizeSlackConfig(this.value),
    };
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        const nextConfig = this.normalizeSlackConfig(value);
        if (JSON.stringify(nextConfig) === JSON.stringify(this.localConfig)) return;

        this.isSyncing = true;
        this.localConfig = nextConfig;
        this.$nextTick(() => {
          this.isSyncing = false;
        });
      },
    },
    localConfig: {
      deep: true,
      handler(config) {
        if (this.isSyncing) return;
        this.$emit('input', this.withSlackConfig(this.value, config));
      },
    },
  },
  methods: {
    normalizeSlackConfig(config) {
      const accountConfig = config && !Array.isArray(config) ? config : {};
      const slackConfig = accountConfig.slackNotifications || {};
      const digestConfig = slackConfig.digest || {};

      return {
        webhookUrl: slackConfig.webhookUrl || accountConfig.slackWebhookUrl || '',
        message: slackConfig.message || DEFAULT_MESSAGE,
        digest: {
          enabled: !!digestConfig.enabled,
          defaultWindowMs: digestConfig.defaultWindowMs || 60000,
          defaultMessage: digestConfig.defaultMessage || DEFAULT_DIGEST_MESSAGE,
          rules: (digestConfig.rules || []).map((rule) => ({
            eventTypes: rule.eventTypes || [],
            windowMs: rule.windowMs || 30000,
            message: rule.message || DEFAULT_DIGEST_MESSAGE,
          })),
        },
      };
    },
    withSlackConfig(config, slackConfig) {
      const nextConfig = { ...(config || {}) };
      nextConfig.slackNotifications = {
        webhookUrl: (slackConfig.webhookUrl || '').trim(),
        message: slackConfig.message || '',
        digest: {
          enabled: !!slackConfig.digest.enabled,
          defaultWindowMs: slackConfig.digest.defaultWindowMs || 60000,
          defaultMessage: slackConfig.digest.defaultMessage || '',
          rules: slackConfig.digest.rules.map((rule) => ({
            eventTypes: rule.eventTypes || [],
            windowMs: rule.windowMs || 30000,
            message: rule.message || '',
          })),
        },
      };
      return nextConfig;
    },
    addRule() {
      this.localConfig.digest.rules.push({
        eventTypes: [],
        windowMs: this.localConfig.digest.defaultWindowMs || 30000,
        message: DEFAULT_DIGEST_MESSAGE,
      });
    },
    removeRule(index) {
      this.localConfig.digest.rules.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.slack-notifications-config {
  margin-top: 18px;
}

.slack-field {
  margin-bottom: 18px;
}

.slack-field label {
  color: #13191d;
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  margin-bottom: 6px;
}

.slack-notifications-config small {
  display: block;
  line-height: 1.4;
  margin-top: 6px;
  opacity: 0.7;
}

.slack-switch-row {
  align-items: center;
  display: flex;
  gap: 10px;
  line-height: 20px;
}

.slack-switch-row span {
  color: #3d8bff;
  font-weight: 600;
}

.slack-notifications-config .el-input-number {
  width: 160px;
}

.slack-digest-rule {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  margin-top: 16px;
  padding-top: 16px;
}

.slack-digest-rule-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}
</style>
